import { Fragment } from 'react';
import { Box, Chip, CircularProgress, List, ListItem, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { Announcement } from 'types/auth';
import AnnouncementTypeChip from './AnnouncementTypeChip';

interface AnnouncementsListProps {
  announcements: Announcement[];
  selectedUid?: string | null;
  loading: boolean;
  onSelect: (announcement: Announcement) => void;
}

export const formatReleaseDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'd MMM yyyy');
};

export default function AnnouncementsList({ announcements, selectedUid, loading, onSelect }: AnnouncementsListProps) {
  return (
    <Box sx={{ position: 'relative' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
              Loading Announcements...
            </Typography>
            <Stack alignItems="center">
              <CircularProgress size={32} color="primary" thickness={4} />
            </Stack>
          </Box>
        </Box>
      )}
      {/* disablePadding: the List's own 8px top/bottom would sit on top of the gutter the
          page already gives this panel, pushing the first card away from the search row. */}
      <List component="nav" disablePadding>
        <Fragment>
          {announcements.length === 0 && !loading && (
            <ListItem sx={{ borderRadius: 0 }}>
              <ListItemText
                primary={
                  <Typography color="text.secondary" variant="caption">
                    Announcements list is empty!
                  </Typography>
                }
              />
            </ListItem>
          )}
          {announcements.map((announcement) => (
            <ListItemButton
              key={announcement.uid}
              // Each row is its own bordered card rather than a flush strip in a divided
              // list. With a type chip and a date under every title the rows are two lines
              // tall, and at that height an unbroken list reads as one block of text — the
              // border is what makes a row look like a thing you can click.
              //
              // Hover and selection both move the border colour instead of flooding the
              // background: the row already carries two coloured chips, and a filled
              // hover state fights with them.
              sx={{
                display: 'block',
                mb: 0.75,
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  bgcolor: 'background.paper',
                  borderColor: 'primary.main',
                  boxShadow: '0 1px 6px rgba(60,60,60,0.10)'
                },
                '&.Mui-selected, &.Mui-selected:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => onSelect(announcement)}
              selected={selectedUid === announcement.uid}
            >
              <ListItemText
                disableTypography
                sx={{ m: 0 }}
                primary={
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography
                        variant="subtitle1"
                        // Unread titles carry the weight — the chip alone is easy to miss
                        // when scanning a long list.
                        fontWeight={announcement.isRead === false ? 600 : 400}
                        color="text.primary"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {announcement.title}
                      </Typography>
                      {/* Straight off the server's per-caller field. With reads happening
                          one at a time and in any order, there is no client-side
                          `createdAt > snapshot` comparison that reproduces this. */}
                      {announcement.isRead === false && (
                        <Chip label="New" color="error" size="small" sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }} />
                      )}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
                      {/* On the date line rather than beside the title. The title row
                          already carries the "New" chip, and at 320px the two together
                          push the title into an ellipsis almost immediately. */}
                      <AnnouncementTypeChip type={announcement.type} size="compact" />
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatReleaseDate(announcement.createdAt)}
                      </Typography>
                    </Stack>
                  </Box>
                }
              />
            </ListItemButton>
          ))}
          {/* The closing Divider is gone: every row now carries its own border, so a rule
              under the last one reads as an empty extra item. */}
        </Fragment>
      </List>
    </Box>
  );
}
